/**
 * @typedef {'Deposit'|'Purchase'|'Direct Debit'|'Fee'|'Interest'|'Refund'|'Reward Earned'|'Reward Redemption'|'Tax'|'Withdrawal'|'Credit'|'Transfer'|'Charge'|'Regular Investment'|'Credit Card Payment'} TransactionType
 * @typedef {'pending'|'confirmed'|'skipped'|'cancelled'} PendingStatus
 * @typedef {'Monthly'|'Quarterly'|'Yearly'} ScheduleFreq
 * @typedef {'same'|'different'} DestDateType
 * @typedef {'source'|'dest'} PairingSide
 */

export const DIRECTION = Object.freeze({
	WITHDRAWAL: 'Withdrawal',
	DEPOSIT: 'Deposit',
	TRANSFER: 'Transfer',
});

/** @type {Record<string, string>} */
export const TRANSACTION_TYPES = Object.freeze({
	Deposit: DIRECTION.DEPOSIT,
	Purchase: DIRECTION.WITHDRAWAL,
	'Direct Debit': DIRECTION.WITHDRAWAL,
	Fee: DIRECTION.WITHDRAWAL,
	Interest: DIRECTION.DEPOSIT,
	Refund: DIRECTION.DEPOSIT,
	'Reward Earned': DIRECTION.DEPOSIT,
	'Reward Redemption': DIRECTION.WITHDRAWAL,
	Tax: DIRECTION.WITHDRAWAL,
	Withdrawal: DIRECTION.WITHDRAWAL,
	Credit: DIRECTION.DEPOSIT,
	Transfer: DIRECTION.TRANSFER,
	Charge: DIRECTION.TRANSFER,
	'Regular Investment': DIRECTION.TRANSFER,
	'Credit Card Payment': DIRECTION.TRANSFER,
});

export const TRANSACTION_TYPE_NAMES = Object.freeze(Object.keys(TRANSACTION_TYPES));

export const TRANSACTION_TYPES_BY_ACCOUNT = Object.freeze({
	'Bank Account': ['Deposit', 'Direct Debit', 'Fee', 'Interest', 'Purchase', 'Refund', 'Tax', 'Withdrawal', 'Transfer', 'Credit Card Payment', 'Credit'],
	'Credit Card': ['Direct Debit', 'Fee', 'Interest', 'Purchase', 'Refund', 'Transfer', 'Credit', 'Charge'],
	'Loan': ['Fee', 'Interest', 'Credit Card Payment'],
	'Investment Account': ['Deposit', 'Fee', 'Interest', 'Purchase', 'Refund', 'Tax', 'Withdrawal', 'Transfer'],
	'Pension': ['Deposit', 'Fee', 'Interest', 'Withdrawal', 'Transfer'],
	'Points/Miles': ['Reward Earned', 'Reward Redemption', 'Transfer', 'Credit Card Payment'],
	'Prepaid Card': ['Deposit', 'Fee', 'Purchase', 'Refund', 'Transfer', 'Credit Card Payment', 'Charge'],
	'Real Estate': ['Deposit', 'Fee', 'Interest', 'Purchase', 'Refund', 'Tax', 'Withdrawal', 'Transfer'],
	'Store Credit': ['Deposit', 'Purchase', 'Refund', 'Transfer'],
});

export const SPECIAL_TRANSACTION_TYPES = Object.freeze({
	Transfer: {
		label: 'Transfer',
		sourceType: () => 'Transfer',
		destType: 'Deposit',
		sourceAccountTypes: ['Bank Account', 'Credit Card', 'Investment Account', 'Pension', 'Prepaid Card', 'Real Estate', 'Store Credit'],
		destAccountTypes: ['Bank Account', 'Credit Card', 'Investment Account', 'Pension', 'Prepaid Card', 'Real Estate', 'Store Credit']
	},
	Charge: {
		label: 'Charge',
		sourceType: (/** @type {string} */ srcAccountType) => srcAccountType === 'Bank Account' ? 'Withdrawal' : 'Purchase',
		destType: 'Charge',
		sourceAccountTypes: ['Bank Account', 'Prepaid Card', 'Credit Card'],
		destAccountTypes: ['Prepaid Card', 'Store Credit']
	},
	'Regular Investment': {
		label: 'Regular Investment',
		sourceType: () => 'Regular Investment',
		destType: 'Deposit',
		sourceAccountTypes: ['Bank Account', 'Credit Card', 'Points/Miles', 'Prepaid Card'],
		destAccountTypes: ['Investment Account', 'Pension']
	},
	'Credit Card Payment': {
		label: 'Credit Card Payment',
		sourceType: () => 'Credit Card Payment',
		destType: 'Credit',
		sourceAccountTypes: ['Bank Account', 'Prepaid Card', 'Points/Miles'],
		destAccountTypes: ['Credit Card']
	}
});

export const DIRECTION_NAMES = Object.freeze(['Withdrawal', 'Deposit', 'Transfer']);

export const TYPE_DIRECTIONS = Object.freeze(
	Object.values(DIRECTION).reduce((/** @type {Record<string, string[]>} */ acc, dir) => {
		acc[dir] = Object.entries(TRANSACTION_TYPES)
			.filter(([_, d]) => d === dir)
			.map(([name]) => name);
		return acc;
	}, {})
);

/**
 * @param {string} type
 * @returns {boolean}
 */
export function isSpecialType(type) {
	return type in SPECIAL_TRANSACTION_TYPES;
}

/**
 * @param {string} type
 * @returns {string | null}
 */
export function getDirectionForType(type) {
	return TRANSACTION_TYPES[type] ?? null;
}

/**
 * @param {string} type
 * @param {string} sourceAccountType
 * @param {string} destAccountType
 * @returns {boolean}
 */
export function isValidSpecialTransfer(type, sourceAccountType, destAccountType) {
	const config = SPECIAL_TRANSACTION_TYPES[/** @type {keyof typeof SPECIAL_TRANSACTION_TYPES} */ (type)];
	if (!config) { return false; }
	return config.sourceAccountTypes.includes(sourceAccountType)
		&& config.destAccountTypes.includes(destAccountType);
}
